import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextField } from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __changeNumberTmobilej } from "../../../../api/sim";

const TmobilejChangeNumberForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [formData, setFormData] = useState(null);

  const { setAlertMessage } = useAlert();
  const nav = useNavigate();

  const [response, call, loading] = useAxios({ ...__changeNumberTmobilej });

  const lineValidationSchema = yup.object({
    number: yup
      .string("number")
      .min(10, "Should be exactly 10 digits long")
      .max(10, "Should be exactly 10 digits long")
      .required(),
  });

  const formik = useFormik({
    initialValues: {},
    onSubmit: (values) => {
      setFormData(values);
    },
    validateOnChange: true,
  });

  const validateFunction = (data) => {
    const errors = {};
    const formattedLines = [];
    if (!data) return errors;

    const numbers = [];
    const lines = data.split("\n");
    lines.some((line, idx) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        return false; // Skip empty lines
      }

      const fields = trimmedLine.split(",").map((f) => f.trim());
      if (fields.length < 1 || !fields[0]) {
        errors.lines = `Invalid format in line ${
          idx + 1
        }: ${line}. Phone number is required.`;
        return true;
      }

      try {
        const phoneNumber = fields[0];
        const obtainedData = { number: phoneNumber };
        lineValidationSchema.validateSync(obtainedData);
        if (isNaN(obtainedData.number)) throw new Error("Invalid Number");

        // Check for duplicate numbers
        if (numbers.indexOf(obtainedData.number) !== -1) {
          errors.lines = `Duplicate number value in line ${idx + 1}`;
          return true;
        }
        numbers.push(obtainedData.number);

        // Parse optional areacode and zipcode
        const phoneData = { number: obtainedData.number };
        if (fields.length > 1 && fields[1]) {
          const areacode = fields[1];
          if (!/^\d{3}$/.test(areacode)) {
            errors.lines = `Invalid area code in line ${
              idx + 1
            }: ${areacode}. Area code must be 3 digits.`;
            return true;
          }
          phoneData.areacode = areacode;
        }
        if (fields.length > 2 && fields[2]) {
          const zipcode = fields[2];
          if (!/^\d{5}$/.test(zipcode)) {
            errors.lines = `Invalid zip code in line ${
              idx + 1
            }: ${zipcode}. Zip code must be 5 digits.`;
            return true;
          }
          phoneData.zipcode = zipcode;
        }

        formattedLines.push(phoneData);
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}. ${
          err.message
        }`;
        return true;
      }
      return false;
    });

    if (!errors.lines) setLinesData(formattedLines);
    return errors;
  };

  useEffect(() => {
    if (formData && linesData.length > 0) {
      call({
        data: {
          phoneNumbers: [...linesData],
        },
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "Change Number queued" });
      setLinesData([]);
      nav("/tmobilej/change_number");
    }
    formik.setSubmitting(false);
  }, [response, setAlertMessage, nav, formik]);

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      setRawData(target.result);
      formik.setErrors({
        ...formik.errors,
        ...validateFunction(target.result),
      });
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    if (!file.name.endsWith(".csv")) {
      formik.setErrors({ ...formik.errors, upload: "Please input a csv file" });
      return;
    }
    handleFile(file);
  };

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Change Number"
          sx={{ mb: 3 }}
          gutterBottom
        />

        <Grid
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item>
            <FormControl
              error={Boolean(formik.errors.upload)}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ mr: 1 }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {formik.errors.upload ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item>
            <FormControl
              error={Boolean(formik.errors.lines)}
              sx={{ width: "100%" }}
            >
              <FormHelperText id="lines-helper-text">
                Add in the format "Phone Number" (10 digits) or "Phone
                Number,AreaCode,ZipCode" (optional)
              </FormHelperText>
              <TextField
                multiline
                minRows={6}
                placeholder={`Add one phone number per line:\n1234567890\n9876543210,201\n5551234567,201,12345`}
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
                sx={{ minWidth: 550 }}
              />
              {formik.errors.lines ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={formik.errors.lines || !linesData.length}
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Change Number"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejChangeNumberForm;
